import { ActivityChangeType } from "@/components/ActivityStream/ActivityStreamItem";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useSaveFile } from "@/hooks/useSaveFile";
import { clearAllInternalErrors, forceReloadIFrame } from "@/store/iframeSlice";
import { useTypedSelector } from "@/store/store";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import {
  ChangeApplyStatus,
  Message,
  MessageContext,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import {
  useLoadCodebase,
  useRunTerminalCommand,
  useStartServer,
} from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import { ChatMessageOptions, PartialKey, UploadedFile } from "@/utils/types";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useCanvasId,
} from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { cloneDeep, merge, pick, throttle } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { applyTextEditorToContents } from "./useApplyChatBlock";
import { getFilesFromTree } from "./utils";
import {
  AssistantInstruction,
  AssistantInstructionError,
  parseAssistantMessage,
  parseMessageContentBlocks,
  TracedAssistantInstruction,
  useCreateMessageForChat,
} from "./utils/messages";
import { useExecuteDbCommand, usePreloadStoryboard } from "./utils/sideEffects";
import { useChatWithAI } from "@/utils/ai/chatWithAI";

// TODO: Refactor out the message context properties like "hadError" and
// "savedActivityId" it is unclear what they are for

// Handles updating the status of messages to update the UI if needed and
// tracking the states in posthog
export const useUpdateMessageContext = () => {
  const setChatMessageContext = useAiActionStore(
    (state) => state.setChatMessageContext
  );

  const updateMessageContext = (
    message: Message,
    updates: Partial<MessageContext>
  ) => {
    const newMessageContext = {
      ...message.message_context,
      ...updates,
    };
    message.message_context = newMessageContext;
    setChatMessageContext(message.id, newMessageContext);
  };

  const updateApplyStatus = (
    message: Message,
    updates: Partial<MessageContext["changeApplyStatus"]>
  ) => {
    updateMessageContext(message, {
      changeApplyStatus: merge(
        cloneDeep(message.message_context?.changeApplyStatus),
        updates
      ),
    });
  };

  const updateMessageStatus = (
    message: Message,
    tracedInstruction: TracedAssistantInstruction,
    applyStatusWithDebug: PartialKey<ChangeApplyStatus, "fixing"> & {
      debug?: string;
    }
  ) => {
    const { traceId, ...instruction } = tracedInstruction;
    const { debug, ...applyStatusPartial } = applyStatusWithDebug;

    if (applyStatusPartial.status === "failed" && !applyStatusPartial.fixing) {
      applyStatusPartial.fixing = false;
    }
    const applyStatus = applyStatusPartial as ChangeApplyStatus;

    let updatedUI = true;
    switch (instruction.type) {
      case "fileChange":
        updateApplyStatus(message, {
          fileChanges: { [instruction.filepath]: applyStatus },
        });
        break;
      case "newStoryboard":
        if (instruction.filepath) {
          updateApplyStatus(message, {
            fileChanges: { [instruction.filepath]: applyStatus },
          });
        }
        break;
      case "newFile":
        updateApplyStatus(message, {
          fileChanges: { [instruction.filepath]: applyStatus },
        });
        break;
      case "sqlMigration":
        updateApplyStatus(message, { runSqlMigration: applyStatus });
        if (applyStatus.status === "succeeded") {
          updateMessageContext(message, { savedActivityId: "RAN_COMMAND" });
        }
        break;
      case "edgeFunctionDeployment":
        updateApplyStatus(message, { deployEdgeFunction: applyStatus });
        if (applyStatus.status === "succeeded") {
          updateMessageContext(message, { savedActivityId: "RAN_COMMAND" });
        }
        break;
      case "convexSync":
        updateApplyStatus(message, { runConvexSync: applyStatus });
        break;
      case "terminalCommand":
        updateApplyStatus(message, {
          terminalCommands: { [instruction.command]: applyStatus },
        });
        if (applyStatus.status === "succeeded") {
          updateMessageContext(message, { savedActivityId: "RAN_COMMAND" });
        }

        break;
      default:
        updatedUI = false;
    }

    track("AI_CHAT_SIDE_EFFECT", {
      ...applyStatus,
      traceId,
      instruction,
      debug,
      updatedUI,
    });
  };

  return { updateMessageContext, updateMessageStatus, updateApplyStatus };
};

export const useSendMessage = () => {
  const canvasId = useCanvasId();
  const chatWithAI = useChatWithAI();
  const { createUserMessage, createAssistantMessage } =
    useCreateMessageForChat();
  const setChatStreamingMessages = useAiActionStore(
    (state) => state.setChatStreamingMessages
  );
  const fileTree = useTypedSelector((state) => state.codebase.fileTree);
  const { runConvexSync, runSqlMigration, deployEdgeFunction } =
    useExecuteDbCommand();
  const { updateMessageContext, updateMessageStatus, updateApplyStatus } =
    useUpdateMessageContext();
  const loadCodebase = useLoadCodebase();
  const restartDevServer = useStartServer();
  const runTerminalCommand = useRunTerminalCommand();
  const { setNewLayout, layoutMode } = useLayoutStore((state) =>
    pick(state, ["setNewLayout", "layoutMode", "leftTab"])
  );
  const { preloadStoryboard, getPreloadedStoryboards } = usePreloadStoryboard();
  const { session } = useSession();

  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);
  const abortController = useRef<AbortController>();
  useEffect(() => {
    if (abortController.current) {
      abortController.current.abort();
    }
  }, [stopAiTrigger]);
  const saveFile = useSaveFile();

  const fileToContents = useCodebaseStore((state) => state.fileToContents);

  const dispatch = useDispatch();
  const clearStoryboardIdToErrors = useDevServerStore(
    (state) => state.clearStoryboardIdToErrors
  );
  const clearTerminalErrors = useDevServerStore(
    (state) => state.clearTerminalErrors
  );
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const persistMessagesToDb = async (newMessages: Message[]) => {
    onDbSnapshot("aiChatMessages", {
      newItems: newMessages,
    });
    await supabaseClient?.from("ai_chat_messages").insert(newMessages as any[]);
  };

  const runTerminalInstruction = async (
    message: Message,
    instruction: AssistantInstruction
  ) => {
    if (instruction.type !== "terminalCommand") {
      return;
    }
    updateMessageStatus(message, instruction, {
      status: "in_progress",
    });
    try {
      const { success, error } = await runTerminalCommand({
        command: instruction.command,
        abortController: abortController.current,
      });

      if (success) {
        updateMessageStatus(message, instruction, {
          status: "succeeded",
        });
      } else {
        updateMessageStatus(message, instruction, {
          status: "failed",
          reason: `Terminal command failed`,
          debug: error,
        });
      }
      return { success, error };
    } catch (e) {
      updateMessageStatus(message, instruction, {
        status: "failed",
        reason: `Terminal command failed`,
        debug: getAxiosErorrMessage(e),
      });
      return { success: false, error: getAxiosErorrMessage(e) };
    }
  };

  const applyFileChange = async ({
    message,
    fileChange,
    filesInContext,
    userPrompt,
    traceId,
    canRetry = true,
  }: {
    message: Message;
    fileChange: AssistantInstruction;
    userPrompt: string;
    filesInContext?: string[];
    traceId?: string;
    canRetry?: boolean;
  }): Promise<
    | { success: true; newContents: string }
    | { success: false; error?: AssistantInstructionError }
  > => {
    if (fileChange.type !== "fileChange") {
      return { success: true, newContents: "" }; // Shouldn't ever happen if this function is called properly
    }

    const codebaseFiles = getFilesFromTree(fileTree, canvasId);
    updateMessageStatus(message, fileChange, { status: "in_progress" });

    // We don't trust any changes to files the AI never saw
    if (
      codebaseFiles?.includes(fileChange.filepath) &&
      !filesInContext?.includes(fileChange.filepath)
    ) {
      updateMessageStatus(message, fileChange, {
        status: "failed",
        reason: "Blind edit",
        debug: "File was changed but not in context",
        fixing: canRetry,
      });
      return {
        success: false,
        error: { type: "blindEdit", filename: fileChange.filepath },
      };
    }

    // We only warn for this since it wouldn't necessarily end up as an
    // incorrect result, ideally our AI wouldn't make this mistake though.
    if (
      fileChange.command === "create" &&
      codebaseFiles?.includes(fileChange.filepath)
    ) {
      track("AI_CHAT_WARNING", {
        warning: "Calling create on a file that already exists",
        data: pick(fileChange, ["filepath"]),
      });
    }

    const updatedFileToContents = useCodebaseStore.getState().fileToContents;
    let contents =
      fileToContents[fileChange.filepath] ||
      updatedFileToContents[fileChange.filepath];

    // This case _should_ never happen, but track it just in case
    if (contents === undefined) {
      track("AI_CHAT_ERROR", {
        error: `File contents not found`,
        data: pick(fileChange, ["command", "filepath"]),
        traceId,
      });
      contents = ""; // TODO: Swap this to success: false once we figure out why this is happening even for files that do exist
      // return { success: false };
    }

    const supabaseJWT = await getUnexpiredToken(session);
    const { newContents, succeeded, error } = await applyTextEditorToContents(
      fileChange,
      contents,
      canvasId,
      supabaseJWT || "",
      {
        useRelaceAsFallback: true,
        originalUserPrompt: userPrompt,
        aiChatTraceId: traceId,
      }
    );

    if (!succeeded) {
      updateMessageStatus(message, fileChange, {
        status: "failed",
        reason: error || "Unknown error",
      });
      return {
        success: false,
        error: {
          type: "textEditorError",
          filename: fileChange.filepath,
          error: error || "Unknown error",
        },
      };
    }
    return { success: true, newContents };
  };

  const { supabaseClient } = useSupabase();
  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);
  const isChatLoading = useIsChatLoading();

  const resetChatToggles = useAiContextStore((state) => state.resetToggles);
  const setChatContext = useAiContextStore((state) => state.setChatContext);

  // Collect the output of the AI, updating the chat view as it streams in
  const sendMessage = async ({
    updateChat = true, // false is used for when running retries to fix existing broken messages
    retryCount = 0,
    refreshAfterResponse = false,
    sendChatMessageFiles,
    canRetry = true, // If this is true message context may be updated with fixing: true
    ...messageOpts
  }: ChatMessageOptions & {
    retryCount?: number;
    updateChat?: boolean;
    refreshAfterResponse?: boolean;
    sendChatMessageFiles?: UploadedFile[];
    canRetry?: boolean;
  }): Promise<Message[]> => {
    if (isChatLoading) {
      track("AI_CHAT_ERROR", {
        error: "sendMessage call while chat is loading",
      });
      return [];
    }

    // The old version of sendMessage took this parameter but treated
    // every value as if it was an image in how it used it in the prompt.
    // TODO: Validate that every use of sendChatMessageFiles is actually an
    // image and then rename everything to just use messageOpts.attachedImages
    // directly, or have a filetype for UploadedFile
    if (sendChatMessageFiles && sendChatMessageFiles.length > 0) {
      messageOpts.attachedImages = (messageOpts.attachedImages || []).concat(
        sendChatMessageFiles
      );
    }

    setChatContext();
    const userMessage = createUserMessage(messageOpts);

    if (updateChat) {
      // Updates the UI
      setChatStreamingMessages([
        ...messageOpts.messagesCanSummarize,
        ...messageOpts.messagesDoNotSummarize,
        userMessage,
      ]);

      await persistMessagesToDb([userMessage]);
    }

    abortController.current = new AbortController();

    const handledMessageSegments = new Set<string>();
    const preprocessStreamedMessage = (message: Message) =>
      throttle(
        () => {
          const newChangeSegments = parseMessageContentBlocks(message)
            .filter((s) => !s.jsonIncomplete)
            .filter((s) => s.type === "aiChanges" && s.aiChanges?.changes)
            .filter((_, i) => {
              if (!handledMessageSegments.has(`${message.id}-${i}`)) {
                handledMessageSegments.add(`${message.id}-${i}`);
                return true;
              }
              return false;
            });

          for (const aiChangeSegment of newChangeSegments) {
            const { changes } = aiChangeSegment.aiChanges || { changes: [] };
            for (const aiChange of changes) {
              if (aiChange.storyboardName) {
                preloadStoryboard({
                  storyboardName: aiChange.storyboardName,
                  width: aiChange.size.width,
                  height: aiChange.size.height,
                  x: aiChange.topLeftCorner.x,
                  y: aiChange.topLeftCorner.y,
                });
              }
            }
          }
        },
        400,
        // trailing: false to avoid weird timing issues, this function should only
        // be optimistic and not be guaranteed to run
        { leading: true, trailing: false }
      );

    // Get the entire response from the AI
    const {
      newMessages,
      error: chatWithAIError,
      filesInContext,
      traceId,
    } = await chatWithAI({
      ...messageOpts,
      abortController: abortController.current,
      onStreamedMessages: (aiMessages: Message[]) => {
        if (updateChat) {
          // Updates the UI
          setChatStreamingMessages([
            ...messageOpts.messagesCanSummarize,
            ...messageOpts.messagesDoNotSummarize,
            userMessage,
            ...aiMessages,
          ]);

          const latestMessage = aiMessages[aiMessages.length - 1];
          if (!latestMessage) {
            return;
          }
          preprocessStreamedMessage(latestMessage);
        }
      },
    });
    const aiResponses = (newMessages || []).map(createAssistantMessage);

    if (session) refreshPlanInfo(session);

    let shouldReloadCodebase = false;
    let shouldRestartDevServer = false;
    let allErrors: AssistantInstructionError[] = [];

    // Apply the changes for each message
    for (const message of aiResponses) {
      if (chatWithAIError) {
        updateMessageContext(message, { hadError: true });
        continue;
      }

      const { instructions, errors } = parseAssistantMessage(message, traceId);

      allErrors = allErrors.concat(errors); // Handle them at the end

      shouldReloadCodebase =
        shouldReloadCodebase ||
        instructions.some((i) => i.type === "reloadCodebase");
      shouldRestartDevServer =
        shouldRestartDevServer ||
        instructions.some((i) => i.type === "restartDevServer");

      const newStoryboards = instructions.filter(
        (i) => i.type === "newStoryboard"
      );
      const newFiles = instructions.filter((i) => i.type === "newFile");
      const fileChanges = instructions.filter((i) => i.type === "fileChange");
      const terminalCommands = instructions.filter(
        (i) => i.type === "terminalCommand"
      );
      const dbCommands = instructions.filter(
        (i) =>
          i.type === "sqlMigration" ||
          i.type === "edgeFunctionDeployment" ||
          i.type === "convexSync"
      );

      // Run terminal commands first so that any new packages are installed
      // prior to file changes so that as the file changes come in the user
      // is more likely to see valid results from their dev server
      if (terminalCommands.length > 0) {
        setNewLayout(LeftTab.AIChat, LayoutMode.Code);
        for (const terminalCommand of terminalCommands) {
          // Errors here might be retryable but that's handled through the "Fix with AI" mechanism detecting errors in the terminal
          await runTerminalInstruction(message, terminalCommand);
        }
        setNewLayout(LeftTab.AIChat, layoutMode);
      }

      // Prepare all file changes in one object to save them all together to
      // avoid the devserver hot reloading and showing a bunch of errors
      let fileContentsForPath: Record<string, string> = {};
      const allFileInstructions = [
        ...newFiles,
        ...fileChanges,
        ...newStoryboards,
      ];

      // Use the preloaded storyboards to find the filenames for them
      let preloadedStoryboards = await getPreloadedStoryboards();
      for (const newStoryboard of newStoryboards) {
        updateMessageStatus(message, newStoryboard, { status: "in_progress" });
        const preloadedStoryboard =
          preloadedStoryboards.find(
            (s) => s.name === newStoryboard.storyboardName
          ) || (await preloadStoryboard(newStoryboard));
        if (!preloadedStoryboard) {
          // Should never happen
          updateMessageStatus(message, newStoryboard, {
            status: "failed",
            reason: "Failed to load storyboard",
          });
          continue;
        }
        const filePath = preloadedStoryboard.fullFilePath;
        fileContentsForPath[filePath] = newStoryboard.contents;
      }

      for (const newFile of newFiles) {
        updateMessageStatus(message, newFile, { status: "in_progress" });
        fileContentsForPath[newFile.filepath] = newFile.content;
      }

      // All file edits using the text editing tools
      for (const fileChange of fileChanges) {
        const result = await applyFileChange({
          message,
          fileChange,
          filesInContext,
          userPrompt: messageOpts.userPrompt,
          traceId: traceId || "",
          canRetry,
        });
        if (result.success) {
          fileContentsForPath[fileChange.filepath] = result.newContents;
        } else if (result.error) {
          allErrors.push(result.error);
        }
      }

      // Save all the files at once
      if (Object.keys(fileContentsForPath).length > 0) {
        try {
          const result = await saveFile({
            fileToContentsToSave: fileContentsForPath,
            saveActivityType: ActivityChangeType.AI_CHANGE,
          });
          updateMessageContext(message, {
            savedActivityId: result?.data?.activityId,
            editedFiles: Object.keys(fileContentsForPath),
          });
          allFileInstructions.forEach((i) => {
            updateMessageStatus(message, i, {
              status: "succeeded",
            });
          });

          // If successfully edited files, clear any old errors
          clearStoryboardIdToErrors();
          dispatch(clearAllInternalErrors());
          clearTerminalErrors();
        } catch (e) {
          allFileInstructions.forEach((i) => {
            updateMessageStatus(message, i, {
              status: "failed",
              reason: "Failed to save files",
            });
          });
          updateMessageContext(message, { hadError: true });
        }
      }

      if (refreshAfterResponse) {
        // Provide a small amount of time for the changes to compile
        setTimeout(() => {
          dispatch(forceReloadIFrame());
        }, 200);
      }

      // Run any database commands last since the latency matters less there compared to file changes
      for (const dbCommand of dbCommands) {
        updateMessageStatus(message, dbCommand, { status: "in_progress" });
        try {
          let result;
          switch (dbCommand.type) {
            case "sqlMigration":
              result = await runSqlMigration(dbCommand.filename);
              break;
            case "edgeFunctionDeployment":
              result = await deployEdgeFunction(dbCommand.filename);
              break;
            case "convexSync":
              result = await runConvexSync();
              break;
          }

          if (!result.success) {
            updateMessageStatus(message, dbCommand, {
              status: "failed",
              reason: `Error during ${dbCommand.type}: ${
                result.error || "Unknown error"
              }`,
              fixing: false,
            });
            continue;
          }
        } catch (e) {
          updateMessageStatus(message, dbCommand, {
            status: "failed",
            reason: `Error during ${dbCommand.type}: ${getAxiosErorrMessage(
              e
            )}`,
            fixing: false,
          });
          continue;
        }
      }
    }

    if (shouldReloadCodebase) {
      loadCodebase(false /* showFlush */, false /* showProgress */);
    }
    if (shouldRestartDevServer) {
      restartDevServer({
        forceRestart: true,
        doNotReloadCodebase: shouldReloadCodebase,
      });
    }

    // Handle any errors, retrying if needed (this will update the messages if updates are needed)
    await processAssistantErrors({
      ...messageOpts,
      userMessage,
      aiResponses,
      errors: allErrors,
      retryCount,
      refreshAfterResponse,
    });

    if (updateChat) {
      await persistMessagesToDb(aiResponses);

      const applyStatuses = aiResponses.flatMap((m) => {
        return Object.values(m.message_context?.changeApplyStatus || {});
      });
      const pendingStatuses = applyStatuses.filter(
        (s) => s.status === "pending"
      );
      if (pendingStatuses.length > 0) {
        track("AI_CHAT_ERROR", {
          error: "Pending statuses remaining",
          data: {
            pendingStatuses,
          },
          traceId,
        });
      }
    }

    if (updateChat) {
      setChatStreamingMessages(null);
      resetChatToggles("all");
    }

    return aiResponses;
  };

  const processAssistantErrors = async ({
    userMessage,
    aiResponses,
    errors,
    retryCount,
    refreshAfterResponse,
    ...messageOpts
  }: ChatMessageOptions & {
    userMessage: Message;
    aiResponses: Message[];
    errors: AssistantInstructionError[];
    retryCount: number;
    refreshAfterResponse: boolean;
  }) => {
    const fixStringsForRetry: string[] = []; // This being non-empty triggers a retry
    const filesWithErrors = new Set<string>();

    // We don't add any fixstrings for these errors to trigger retrying because
    // retrying an already too long request wouldn't work anyway
    const incompleteJsonErrors = errors.filter(
      (e) => e.type === "incompleteSegmentJson"
    );
    for (const error of incompleteJsonErrors) {
      track("AI_CHAT_WARNING", {
        warning: "Incomplete JSON for Segment",
        data: error.segment,
      });

      // We likely ran out in the middle of a file, can at least inform the user of what wasn't completed.
      if (error.filepath) {
        updateApplyStatus(error.message, {
          fileChanges: {
            [error.filepath]: {
              status: "failed",
              reason:
                "Maximum output token limit hit. Please try again with a smaller change or regenerate the code for the affected file by clicking the button.",
              fixing: false,
              isOutputTokenLimitHit: true,
              continuationPrompt: `Regenerate the changes for file ${error.filepath}`,
            },
          },
        });
      }
    }

    const packageJsonErrors = errors.filter(
      (e) => e.type === "packageJsonChange"
    );
    if (packageJsonErrors.length > 0) {
      fixStringsForRetry.push(`Editing package.json directly is not allowed. Please use terminal command to install the required libraries.

Use the appropriate package manager to install the required libraries.

For example, if the project is using npm, you can run the following command:
"npm install <package-name>"

Similarly, if the project is using pnpm, you can run the following command:
"pnpm add <package-name>"`);

      for (const error of packageJsonErrors) {
        filesWithErrors.add(error.filename);
      }
    }

    const textEditorErrors = errors.filter((e) => e.type === "textEditorError");
    if (textEditorErrors.length > 0) {
      fixStringsForRetry.push(
        `There was a mistake in the output in the last message, remake the changes for the following sections:\n\nErrors: ${textEditorErrors
          .map((f) => {
            return `${f.filename}: ${f.error}\n`;
          })
          .join(", ")}`
      );
      for (const error of textEditorErrors) {
        filesWithErrors.add(error.filename);
      }
    }

    const brokenSegmentJsonErrors = errors.filter(
      (e) => e.type === "brokenSegmentJson"
    );
    if (brokenSegmentJsonErrors.length > 0) {
      const brokenSegments = brokenSegmentJsonErrors.map((e) => e.segment);
      fixStringsForRetry.push(
        `The JSON format for these entire sections could not be parsed: ${brokenSegments
          .map((s) => s.content)
          .join(", ")}`
      );
    }

    const blindEditErrors = errors.filter((e) => e.type === "blindEdit");
    if (blindEditErrors.length > 0) {
      fixStringsForRetry.push(
        `You have attempted to modify files that were not included in the context: [${blindEditErrors
          .map((e) => e.filename)
          .join(
            ", "
          )}]. I am now explicitly including them in the context. Regenerate the code by accounting for the file contents provided in the context.`
      );
      for (const error of blindEditErrors) {
        filesWithErrors.add(error.filename);
      }
    }

    // Nothing worth retrying
    if (fixStringsForRetry.length === 0) {
      return;
    }

    const fixString = fixStringsForRetry.join("\n\n");

    track("AI_PROMPT_FIX_DIFF", {
      prompt: messageOpts.userPrompt,
      filesWithErrors: Array.from(filesWithErrors),
      fixString,
      totalFiles: Object.keys(fileToContents).length,
      sendChatMessageFiles: userMessage.message_context?.files || [],
      instructionErrors: errors,
      messageIds: aiResponses.map((m) => m.id),
      retryCount,
    });

    let maxRetries = 1;
    if (retryCount >= maxRetries) {
      return;
    }

    let aiFixMessages: Message[] = [];
    try {
      aiFixMessages = await sendMessage({
        ...messageOpts,
        messagesCanSummarize: messageOpts.messagesCanSummarize,
        messagesDoNotSummarize: [
          ...messageOpts.messagesDoNotSummarize,
          userMessage,
          ...aiResponses,
        ],
        userPrompt: fixString,
        retryCount: retryCount + 1,
        canRetry: retryCount + 1 < maxRetries,
        refreshAfterResponse,
        isFreePrompt: true,
        updateChat: false, // Don't update the chat this time, we'll handle the messages
        disableTextEditor: true, // Force the AI to give full file contents to give it more time to think

        // These should have already been done for the first attempt
        useSearch: false,
        useReasoning: false,
      });
    } catch (e) {
      track("AI_CHAT_ERROR", {
        error: `Failure during retry: ${getAxiosErorrMessage(e)}`,
        data: {
          error: e,
          messageOpts,
        },
      });
    }

    const fixFileChanges = aiFixMessages.reduce((acc, m) => {
      const fileChanges =
        m.message_context?.changeApplyStatus?.fileChanges || {};
      return { ...acc, ...fileChanges };
    }, {});

    let filesWithReflectedStatus = new Set<string>();

    // Updating existing status messages when possible
    for (const initialResponse of aiResponses) {
      const initialFileChanges =
        initialResponse.message_context?.changeApplyStatus?.fileChanges || {};
      for (const filename of Object.keys(initialFileChanges)) {
        if (fixFileChanges[filename]) {
          updateApplyStatus(initialResponse, {
            fileChanges: { [filename]: fixFileChanges[filename] },
          });
          filesWithReflectedStatus.add(filename);
        }
      }
    }

    // Some files may be new after the AI does its second attempt
    const newFileStatuses = Object.keys(fixFileChanges).filter(
      (filename) => !filesWithReflectedStatus.has(filename)
    );
    const lastMessage = aiResponses[aiResponses.length - 1];
    for (const filename of newFileStatuses) {
      updateApplyStatus(lastMessage, {
        fileChanges: { [filename]: fixFileChanges[filename] },
      });
    }
  };

  return { sendMessage };
};
